import { useState, useEffect, useMemo } from 'react'
import { 
  useGetWatchlistByIdQuery, 
  GetWatchlistByIdQuery,
  WatchlistObject 
} from '../../../../generated/graphql'
import { graphqlRequestClient } from '../../../../queries/client'

export const useWatchlistDetails = (watchlistId: number | null) => {
  const [watchlistDetails, setWatchlistDetails] = useState<WatchlistObject | null>(null)
  const [previousWatchlistId, setPreviousWatchlistId] = useState<number | null>(null)
  
  const { data, isLoading, error, isFetching } = useGetWatchlistByIdQuery<GetWatchlistByIdQuery, Error>(
    graphqlRequestClient,
    { input: { id: watchlistId } },
    {
      enabled: !!watchlistId, // Only run query when watchlistId is provided
      refetchOnWindowFocus: false,
      keepPreviousData: true, // Keep previous data while loading new data
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    }
  )

  // Memoize the watchlist details to prevent unnecessary re-renders
  const memoizedWatchlistDetails = useMemo(() => {
    if (data?.newWatchlists && data.newWatchlists.length > 0) {
      const matchingWatchlist = data.newWatchlists.find(w => w?.id === watchlistId)
      return matchingWatchlist as WatchlistObject
    }
    return null
  }, [data, watchlistId])

  useEffect(() => {
    // Only update state if we have new data or if watchlistId changed
    if (memoizedWatchlistDetails) {
      setWatchlistDetails(memoizedWatchlistDetails)
      setPreviousWatchlistId(watchlistId)
    } else if (watchlistId !== previousWatchlistId) {
      // Only clear if we're switching to a different watchlist
      setWatchlistDetails(null)
      setPreviousWatchlistId(watchlistId)
    }
  }, [memoizedWatchlistDetails, watchlistId, previousWatchlistId])

  // Only show loading for initial load, not for subsequent fetches
  const isInitialLoading = isLoading && !watchlistDetails && watchlistId === previousWatchlistId

  return {
    watchlistDetails,
    isLoading: isInitialLoading,
    isFetching,
    error,
    setWatchlistDetails
  }
}
